import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import PharmeasyLayout from "../../components/PharmeasyLayout";
import InwardScanner from "../../components/pharmeasy/InwardScanner";
import {
  ArrowLeft, Download, Clock, Truck, Package, MapPin, Navigation,
  ChevronDown, ChevronUp, List, Map as MapIcon, Play, Loader2, ScanBarcode, StopCircle, CheckCircle2, AlertTriangle,
  ExternalLink, Home, IndianRupee, Copy, Send, UserCheck, RotateCcw, History, RefreshCw, Plus, Trash2
} from "lucide-react";
import {
  buildOverallSessionMapBlobUrl,
  TRIP_HEX_COLORS,
} from "@/utils/mapHelpers";

// Browser Maps key — inlined into the bundle at build time, so not secret.
// Value comes from REACT_APP_GOOGLE_MAPS_KEY (.env.local); the key is restricted
// by HTTP referrer + allowed APIs in Google Cloud Console.
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const InwardSessionDetail = ({ user, onLogout }) => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedTrips, setExpandedTrips] = useState({});
  const [tripViewMode, setTripViewMode] = useState({}); // 'list' or 'map'
  const [exporting, setExporting] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [inwardingActive, setInwardingActive] = useState(false);
  const [forceCompleteModalOpen, setForceCompleteModalOpen] = useState(false);
  const [forceCompleteReason, setForceCompleteReason] = useState("");
  const [forceCompleting, setForceCompleting] = useState(false);
  const [creatingBoltTrips, setCreatingBoltTrips] = useState(false);
  const [creatingBoltTrip, setCreatingBoltTrip] = useState({}); // per-trip loading state
  const [assigningRider, setAssigningRider] = useState({}); // per-trip rider assignment loading
  const [riders, setRiders] = useState([]);
  const [selectedRider, setSelectedRider] = useState({}); // per-trip selected rider
  const [reassigningTrip, setReassigningTrip] = useState({}); // per-trip reassign mode toggle
  const [showHistory, setShowHistory] = useState({}); // per-trip history toggle
  const [addingShipment, setAddingShipment] = useState({}); // per-trip add shipment mode
  const [addShipmentCode, setAddShipmentCode] = useState({}); // per-trip shipment code input
  const [addShipmentLoading, setAddShipmentLoading] = useState({});
  const [showOverallMap, setShowOverallMap] = useState(true);
  const [removeConfirm, setRemoveConfirm] = useState(null); // { tripNumber, shipmentCode, shipmentId }
  const [removingShipment, setRemovingShipment] = useState(false);
  const [showModLog, setShowModLog] = useState({}); // per-trip modification log toggle
  const [deleteTrip, setDeleteTrip] = useState(null); // trip_number to confirm delete
  const [deletingTrip, setDeletingTrip] = useState(false);
  
  const isAdmin = user?.role === "admin" || user?.role === "admin_user";
  const isAnyBoltCreating = creatingBoltTrips || Object.values(creatingBoltTrip).some(Boolean);


  // Helper: compute duration string from two ISO timestamps
  const formatInwardTime = (firstScan, lastScan) => {
    if (!firstScan || !lastScan) return null;
    const start = new Date(firstScan);
    const end = new Date(lastScan);
    const diffMs = Math.abs(end - start);
    const hours = Math.floor(diffMs / 3600000);
    const mins = Math.floor((diffMs % 3600000) / 60000);
    const secs = Math.floor((diffMs % 60000) / 1000);
    if (hours > 0) return `${hours}h ${mins}m ${secs}s`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  useEffect(() => {
    fetchSessionDetail();
    fetchRiders();
  }, [sessionId]);

  const fetchRiders = async () => {
    try {
      const res = await axios.get(`${API}/pharmeasy-orders/riders?size=200`);
      setRiders(res.data?.data || []);
    } catch (e) {
      console.error("Failed to fetch riders:", e);
    }
  };

  const fetchSessionDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inward/session/${sessionId}`);
      setSession(res.data);
      // Initialize view modes
      const modes = {};
      res.data.assignments?.forEach((_, idx) => {
        modes[idx] = 'list';
      });
      setTripViewMode(modes);
      
      // Check if session is in progress
      if (res.data.status === "IN_PROGRESS") {
        setInwardingActive(true);
      }
    } catch (e) {
      toast.error("Failed to load session details");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const handleStartInward = () => {
    setInwardingActive(true);
    setScannerOpen(true);
  };

  const handleStopInward = () => {
    setInwardingActive(false);
    setScannerOpen(false);
  };

  const handleScanSuccess = (scanResult) => {
    // Update local session data to reflect the new inward
    setSession(prev => {
      if (!prev) return prev;
      
      const updatedInwardedShipments = [
        ...(prev.inwarded_shipments || []),
        {
          shipment_id: scanResult.shipment_id,
          scanned_at: new Date().toISOString(),
        }
      ];
      
      return {
        ...prev,
        inwarded_shipments: updatedInwardedShipments,
        status: updatedInwardedShipments.length >= prev.total_shipments ? "COMPLETED" : "IN_PROGRESS",
      };
    });
    
    toast.success(`Inwarded: ${scanResult.location_name} / ${scanResult.sublocation_name}`);
  };

  const handleForceComplete = async () => {
    setForceCompleting(true);
    try {
      const res = await axios.post(`${API}/inward/session/${sessionId}/force-complete`, {
        reason: forceCompleteReason,
      });
      
      toast.success("Session marked as force completed");
      setForceCompleteModalOpen(false);
      setForceCompleteReason("");
      
      // Update local session state
      setSession(prev => ({
        ...prev,
        status: "FORCE_COMPLETED",
        force_completed_at: res.data.force_completed_at,
        force_completed_by: res.data.force_completed_by,
      }));
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Failed to force complete session";
      toast.error(errorMsg);
    } finally {
      setForceCompleting(false);
    }
  };

  const handleReopenSession = async () => {
    if (!window.confirm("Reopen this session to allow scanning of remaining parcels?")) return;
    try {
      await axios.post(`${API}/inward/session/${sessionId}/reopen`);
      toast.success("Session reopened — you can now scan remaining parcels");
      setSession(prev => ({ ...prev, status: "PLANNED" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reopen session");
    }
  };

  const handleCreateBoltTrips = async () => {
    if (!window.confirm("This will create all trips in Pharmeasy BOLT one by one. Continue?")) return;
    
    setCreatingBoltTrips(true);
    try {
      const res = await axios.post(`${API}/inward/session/${sessionId}/create-bolt-trips`);
      const { created, skipped, failed, trip_results } = res.data;
      
      if (failed > 0) {
        toast.warning(`Created ${created}, Skipped ${skipped}, Failed ${failed} trips`);
      } else {
        toast.success(`Created ${created} trips in Pharmeasy (${skipped} already existed)`);
      }
      
      // Log per-trip IDs
      trip_results?.forEach(t => {
        if (t.bolt_trip_id) {
          console.log(`Trip #${t.trip_number} → Bolt #${t.bolt_trip_id}`);
        }
      });
      
      fetchSessionDetail();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create BOLT trips");
    } finally {
      setCreatingBoltTrips(false);
    }
  };

  const handleCreateSingleBoltTrip = async (tripNumber) => {
    setCreatingBoltTrip(prev => ({ ...prev, [tripNumber]: true }));
    try {
      const res = await axios.post(`${API}/inward/session/${sessionId}/create-bolt-trip/${tripNumber}`);
      const data = res.data;
      if (data.bolt_trip_id) {
        toast.success(`Trip #${tripNumber} created in Pharmeasy! Bolt Trip ID: ${data.bolt_trip_id}`);
      } else {
        toast.success(`Trip #${tripNumber} created in Pharmeasy! (${data.reference_ids_count} shipments)`);
      }
      // Refresh session to show bolt_trip_id
      fetchSessionDetail();
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Failed to create trip in Pharmeasy";
      toast.error(`Trip #${tripNumber}: ${errorMsg}`);
    } finally {
      setCreatingBoltTrip(prev => ({ ...prev, [tripNumber]: false }));
    }
  };

  const handleAssignRider = async (tripNumber, boltTripId) => {
    const sprinterId = selectedRider[tripNumber];
    if (!sprinterId) {
      toast.error("Please select a rider first");
      return;
    }
    setAssigningRider(prev => ({ ...prev, [tripNumber]: true }));
    try {
      const res = await axios.post(`${API}/inward/session/${sessionId}/assign-rider/${tripNumber}`, {
        bolt_trip_id: boltTripId,
        sprinter_id: sprinterId,
      });
      const rider = riders.find(r => r.bolt_rider_id === sprinterId);
      toast.success(`Rider ${rider?.name || sprinterId} assigned to Bolt Trip ${boltTripId}`);
      setReassigningTrip(prev => ({ ...prev, [tripNumber]: false }));
      setSelectedRider(prev => ({ ...prev, [tripNumber]: "" }));
      fetchSessionDetail();
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Failed to assign rider";
      toast.error(`Trip #${tripNumber}: ${errorMsg}`);
    } finally {
      setAssigningRider(prev => ({ ...prev, [tripNumber]: false }));
    }
  };

  const handleAddShipmentToTrip = async (tripNumber) => {
    const code = (addShipmentCode[tripNumber] || "").trim();
    if (!code) return toast.error("Enter a shipment code");
    setAddShipmentLoading(prev => ({ ...prev, [tripNumber]: true }));
    try {
      const res = await axios.post(`${API}/inward/session/${sessionId}/trip/${tripNumber}/add-shipment`, {
        shipment_code: code,
      });
      const msg = res.data.message || `Shipment ${code} added to Trip #${tripNumber} at ${res.data.sublocation}`;
      toast.success(msg, { duration: 5000 });
      setAddShipmentCode(prev => ({ ...prev, [tripNumber]: "" }));
      setAddingShipment(prev => ({ ...prev, [tripNumber]: false }));
      fetchSessionDetail();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add shipment");
    } finally {
      setAddShipmentLoading(prev => ({ ...prev, [tripNumber]: false }));
    }
  };

  const handleRemoveShipment = async () => {
    if (!removeConfirm) return;
    setRemovingShipment(true);
    try {
      await axios.post(`${API}/inward/session/${sessionId}/trip/${removeConfirm.tripNumber}/remove-shipment`, {
        shipment_code: removeConfirm.shipmentCode,
      });
      toast.success(`Shipment ${removeConfirm.shipmentCode} removed from Trip #${removeConfirm.tripNumber}`);
      setRemoveConfirm(null);
      fetchSessionDetail();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove shipment");
    } finally {
      setRemovingShipment(false);
    }
  };

  const handleDeleteTrip = async () => {
    if (deleteTrip === null) return;
    setDeletingTrip(true);
    try {
      const res = await axios.post(`${API}/inward/session/${sessionId}/trip/${deleteTrip}/delete-trip`);
      toast.success(res.data.message || `Trip #${deleteTrip} deleted`);
      setDeleteTrip(null);
      fetchSessionDetail();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete trip");
    } finally {
      setDeletingTrip(false);
    }
  };

  const exportSessionToCSV = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/inward/session/${sessionId}/export`);
      const { data, session_code } = res.data;
      
      if (!data || data.length === 0) {
        toast.error("No data to export");
        return;
      }
      
      const headers = [
        "Inward Session ID", "Hub", "Created At", "Created By", "Inwarded",
        "Trip Number", "Pincodes", "Shipment ID", "Package ID", "Reference ID",
        "LBN", "Address", "Slot", "Map Link", "Is Inwarded"
      ];
      
      const csvRows = [headers.join(",")];
      
      data.forEach(row => {
        const values = [
          row.inward_session_id,
          row.hub,
          row.created_at,
          row.created_by,
          row.inwarded_count,
          row.trip_number,
          `"${(row.pincodes || '').replace(/"/g, '""')}"`,
          row.shipment_id,
          row.package_id,
          `"${(row.reference_id || '').replace(/"/g, '""')}"`,
          row.lbn,
          `"${(row.address || '').replace(/"/g, '""')}"`,
          row.slot,
          `"${(row.map_link || '').replace(/"/g, '""')}"`,
          row.is_inwarded ? "Yes" : "No"
        ];
        csvRows.push(values.join(","));
      });
      
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.href = url;
      link.download = `inward_session_${session_code}_export.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${data.length} rows successfully`);
    } catch (e) {
      toast.error("Failed to export session data");
    } finally {
      setExporting(false);
    }
  };

  const getMapLink = (shipment) => {
    const calcCoords = shipment?.calculated_dest_address_coordinate;
    if (calcCoords?.success && calcCoords?.coordinates) {
      return `https://www.google.com/maps?q=${calcCoords.coordinates.lat},${calcCoords.coordinates.lng}`;
    }
    const destAddr = shipment?.dest_address;
    if (destAddr?.latitude && destAddr?.longitude) {
      const lat = parseFloat(destAddr.latitude);
      const lng = parseFloat(destAddr.longitude);
      if (lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng)) {
        return `https://www.google.com/maps?q=${lat},${lng}`;
      }
    }
    return null;
  };

  const formatAddress = (destAddr) => {
    if (!destAddr) return "—";
    const parts = [destAddr.address1, destAddr.city, destAddr.pincode].filter(Boolean);
    return parts.join(", ") || "—";
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  // Copy to clipboard function
  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  // Build Google Maps Embed URL with directions for interactive map
  const buildDirectionsEmbedUrl = (shipments) => {
    const waypoints = shipments
      .map(s => {
        const calc = s.calculated_dest_address_coordinate;
        if (calc?.success && calc?.coordinates) {
          return `${calc.coordinates.lat},${calc.coordinates.lng}`;
        }
        return null;
      })
      .filter(Boolean);
    
    if (waypoints.length === 0) return null;
    
    // Use hub as origin if available
    const hubCoords = session?.hub_coordinates;
    const origin = hubCoords?.lat && hubCoords?.lng
      ? `${hubCoords.lat},${hubCoords.lng}`
      : waypoints[0];
    const destination = waypoints[waypoints.length - 1];
    const intermediate = hubCoords?.lat && hubCoords?.lng
      ? waypoints.slice(0, -1)
      : waypoints.slice(1, -1);
    const cappedWaypoints = intermediate.length > 20 
      ? intermediate.filter((_, i) => i % Math.ceil(intermediate.length / 20) === 0).slice(0, 20)
      : intermediate;
    const waypointsStr = cappedWaypoints.join('|');
    
    let url = `https://www.google.com/maps/embed/v1/directions?key=${GOOGLE_MAPS_API_KEY}&origin=${origin}&destination=${destination}`;
    if (waypointsStr) {
      url += `&waypoints=${waypointsStr}`;
    }
    url += `&mode=driving`;
    
    return url;
  };

  // Build Google Maps URL for viewing all markers (for static fallback)
  const buildStaticMapUrl = (shipments, zoom = 12) => {
    const markers = shipments
      .map((s, idx) => {
        const calc = s.calculated_dest_address_coordinate;
        if (calc?.success && calc?.coordinates) {
          return `markers=color:red%7Clabel:${idx+1}%7C${calc.coordinates.lat},${calc.coordinates.lng}`;
        }
        return null;
      })
      .filter(Boolean);
    
    if (markers.length === 0) return null;
    
    // Add hub marker if available
    const hubCoords = session?.hub_coordinates;
    if (hubCoords?.lat && hubCoords?.lng) {
      markers.unshift(`markers=color:green%7Clabel:H%7C${hubCoords.lat},${hubCoords.lng}`);
    }
    
    // Calculate center from all coordinates
    let totalLat = 0, totalLng = 0, count = 0;
    shipments.forEach(s => {
      const calc = s.calculated_dest_address_coordinate;
      if (calc?.success && calc?.coordinates) {
        totalLat += calc.coordinates.lat;
        totalLng += calc.coordinates.lng;
        count++;
      }
    });
    if (hubCoords?.lat && hubCoords?.lng) {
      totalLat += hubCoords.lat;
      totalLng += hubCoords.lng;
      count++;
    }
    
    const centerLat = totalLat / count;
    const centerLng = totalLng / count;
    
    return `https://maps.googleapis.com/maps/api/staticmap?center=${centerLat},${centerLng}&zoom=${zoom}&size=800x400&maptype=roadmap&${markers.join("&")}&key=${GOOGLE_MAPS_API_KEY}`;
  };

  if (loading) {
    return (
      <PharmeasyLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </PharmeasyLayout>
    );
  }

  if (!session) {
    return (
      <PharmeasyLayout user={user} onLogout={onLogout}>
        <div className="text-center py-12">
          <p className="text-slate-500">Session not found</p>
          <Button variant="outline" onClick={() => navigate("/pharmeasy/hub-ops")} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Hub Ops
          </Button>
        </div>
      </PharmeasyLayout>
    );
  }

  const inwardedCount = session.inwarded_shipments?.length || 0;
  const totalShipments = session.total_shipments || 0;
  // Count RETURN shipments that don't need inward scanning
  const returnCount = (session.assignments || []).reduce((acc, a) => 
    acc + (a.sublocation_assignments || []).filter(s => s.is_return || s.shipment_type === "RETURN").length, 0
  );
  const effectiveInwardedCount = inwardedCount + returnCount;
  const progressPercent = totalShipments > 0 ? (effectiveInwardedCount / totalShipments) * 100 : 0;

  return (
    <PharmeasyLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header - Mobile Responsive */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate("/pharmeasy/hub-ops")} className="w-fit">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-lg md:text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Clock className="w-5 h-5 md:w-6 md:h-6 text-emerald-600" />
                <span className="truncate">Session {session.session_code}</span>
              </h1>
              <p className="text-sm text-slate-500">
                Created {formatDate(session.created_at)}
                {session.inward_first_scan && session.inward_last_scan && (
                  <span className="ml-3 text-emerald-600 font-medium">
                    Inward Time: {formatInwardTime(session.inward_first_scan, session.inward_last_scan)}
                  </span>
                )}
              </p>
            </div>
          </div>
          
          {/* Action Buttons - Stack on mobile */}
          <div className="flex flex-wrap items-center gap-3 md:gap-4">
            {session.status === "COMPLETED" ? (
              <>
                <Badge className="bg-emerald-100 text-emerald-700 px-3 md:px-4 py-1.5 md:py-2">
                  <CheckCircle2 className="w-4 h-4 mr-1 md:mr-2" /> All Inwarded
                </Badge>
                {(() => {
                  const remaining = (session.assignments || []).filter(a => !a.bolt_trip_id && !a.deleted).length;
                  return remaining > 0 ? (
                    <Button 
                      onClick={handleCreateBoltTrips}
                      disabled={isAnyBoltCreating}
                      className="flex items-center gap-1.5 md:gap-2 text-sm bg-blue-600 hover:bg-blue-700"
                      data-testid="create-bolt-trips-btn"
                    >
                      {isAnyBoltCreating ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      <span className="hidden sm:inline">{isAnyBoltCreating ? "Creating..." : `Create ${remaining} Trip${remaining > 1 ? 's' : ''} in Pharmeasy`}</span>
                      <span className="sm:hidden">{isAnyBoltCreating ? "..." : `Create ${remaining}`}</span>
                    </Button>
                  ) : (
                    <Badge className="bg-blue-100 text-blue-700 px-3 md:px-4 py-1.5 md:py-2">
                      <Send className="w-4 h-4 mr-1 md:mr-2" /> All Trips Created in BOLT
                    </Badge>
                  );
                })()}
              </>
            ) : session.status === "FORCE_COMPLETED" ? (
              <>
                <Badge className="bg-amber-100 text-amber-700 px-3 md:px-4 py-1.5 md:py-2">
                  <AlertTriangle className="w-4 h-4 mr-1 md:mr-2" /> Force Completed
                </Badge>
                {(() => {
                  const remaining = (session.assignments || []).filter(a => !a.bolt_trip_id && !a.deleted).length;
                  return remaining > 0 ? (
                    <Button 
                      onClick={handleCreateBoltTrips}
                      disabled={isAnyBoltCreating}
                      className="flex items-center gap-1.5 md:gap-2 text-sm bg-blue-600 hover:bg-blue-700"
                      data-testid="create-bolt-trips-btn"
                    >
                      {isAnyBoltCreating ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      <span className="hidden sm:inline">{isAnyBoltCreating ? "Creating..." : `Create ${remaining} Trip${remaining > 1 ? 's' : ''} in Pharmeasy`}</span>
                      <span className="sm:hidden">{isAnyBoltCreating ? "..." : `Create ${remaining}`}</span>
                    </Button>
                  ) : (
                    <Badge className="bg-blue-100 text-blue-700 px-3 md:px-4 py-1.5 md:py-2">
                      <Send className="w-4 h-4 mr-1 md:mr-2" /> All Trips Created in BOLT
                    </Badge>
                  );
                })()}
              </>
            ) : inwardingActive ? (
              <>
                <Button 
                  onClick={() => setScannerOpen(true)}
                  style={{ backgroundColor: "#1B8C3D" }} 
                  className="flex items-center gap-1.5 md:gap-2 text-sm"
                  data-testid="open-scanner-btn"
                >
                  <ScanBarcode className="w-4 h-4" /> <span className="hidden sm:inline">Open</span> Scanner
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleStopInward}
                  className="flex items-center gap-1.5 md:gap-2 text-sm text-red-600 border-red-300 hover:bg-red-50"
                  data-testid="stop-inward-btn"
                >
                  <StopCircle className="w-4 h-4" /> Stop
                </Button>
                {isAdmin && (
                  <Button 
                    variant="outline"
                    onClick={() => setForceCompleteModalOpen(true)}
                    className="flex items-center gap-1.5 md:gap-2 text-sm text-amber-600 border-amber-300 hover:bg-amber-50"
                    data-testid="force-complete-btn"
                  >
                    <AlertTriangle className="w-4 h-4" /> <span className="hidden sm:inline">Force</span> Complete
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button 
                  onClick={handleStartInward}
                  style={{ backgroundColor: "#1B8C3D" }} 
                  className="flex items-center gap-1.5 md:gap-2 text-sm"
                  data-testid="start-inward-btn"
                >
                  <Play className="w-4 h-4" /> Start Inward
                </Button>
                {isAdmin && session.status !== "COMPLETED" && (
                  <Button 
                    variant="outline"
                    onClick={() => setForceCompleteModalOpen(true)}
                    className="flex items-center gap-1.5 md:gap-2 text-sm text-amber-600 border-amber-300 hover:bg-amber-50"
                    data-testid="force-complete-btn"
                  >
                    <AlertTriangle className="w-4 h-4" /> <span className="hidden sm:inline">Force</span> Complete
                  </Button>
                )}
              </>
            )}
            <Button variant="outline" onClick={exportSessionToCSV} disabled={exporting} className="text-sm">
              <Download className="w-4 h-4 mr-1.5 md:mr-2" />
              <span className="hidden sm:inline">{exporting ? "Exporting..." : "Export CSV"}</span>
              <span className="sm:hidden">CSV</span>
            </Button>
          </div>
        </div>

        {/* Summary Cards - Responsive Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          <Card>
            <CardContent className="pt-4 px-4">
              <p className="text-xs text-slate-500 uppercase flex items-center gap-1">
                <Home className="w-3 h-3" /> Pickup (Hub)
              </p>
              <p className="text-sm md:text-lg font-bold text-slate-800 truncate">{session.hub_name || "—"}</p>
              <p className="text-xs text-emerald-600 mt-0.5">Route starts from hub</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 px-4">
              <p className="text-xs text-slate-500 uppercase">Total Trips</p>
              <p className="text-xl md:text-2xl font-bold text-blue-600">{session.assignments?.length || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 px-4">
              <p className="text-xs text-slate-500 uppercase">Total Shipments</p>
              <p className="text-xl md:text-2xl font-bold text-slate-800">{totalShipments}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 px-4">
              <p className="text-xs text-slate-500 uppercase">Status</p>
              <Badge className={`text-xs ${
                session.status === "COMPLETED" ? "bg-emerald-100 text-emerald-700" : 
                session.status === "FORCE_COMPLETED" ? "bg-amber-100 text-amber-700" :
                session.status === "IN_PROGRESS" ? "bg-blue-100 text-blue-700" : 
                "bg-slate-100 text-slate-700"
              }`}>
                {session.status === "FORCE_COMPLETED" ? "Force" : session.status}
              </Badge>
              {session.status === "FORCE_COMPLETED" && session.force_completed_by && (
                <p className="text-xs text-slate-400 mt-1 truncate">By: {session.force_completed_by}</p>
              )}
            </CardContent>
          </Card>
          <Card className="col-span-2 sm:col-span-1">
            <CardContent className="pt-4 px-4">
              <p className="text-xs text-slate-500 uppercase">Inwarded</p>
              <p className="text-sm md:text-lg font-bold text-emerald-600">
                {inwardedCount}/{totalShipments - returnCount}
                {returnCount > 0 && (
                  <span className="text-purple-600 text-xs md:text-sm font-normal ml-1">+ {returnCount} pickup</span>
                )}
              </p>
              <Progress value={progressPercent} className="h-1.5 md:h-2 mt-1.5 md:mt-2" />
            </CardContent>
          </Card>
        </div>

        {/* Overall Session Map */}
        <Card data-testid="session-overall-map">
          <div
            className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50 border-b"
            onClick={() => setShowOverallMap(p => !p)}
          >
            <div className="flex items-center gap-2">
              <MapIcon className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-slate-700">Overall Session Map</span>
              <Badge variant="outline" className="text-xs">{session.assignments?.length || 0} trips</Badge>
            </div>
            {showOverallMap ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
          {showOverallMap && (
            <CardContent className="p-4">
              {(() => {
                const hubCoords = session.hub_coordinates;
                const hubInfo = hubCoords?.lat && hubCoords?.lng
                  ? { lat: hubCoords.lat, lng: hubCoords.lng, name: session.hub_name, address: session.hub_address }
                  : null;
                const mapUrl = buildOverallSessionMapBlobUrl(session.assignments || [], hubInfo);
                if (mapUrl) {
                  return (
                    <iframe
                      src={mapUrl}
                      width="100%"
                      height="480"
                      style={{ border: 0, borderRadius: 8 }}
                      allowFullScreen=""
                      loading="lazy"
                      title="Overall Session Map"
                      data-testid="session-overall-map-iframe"
                    />
                  );
                }
                return (
                  <div className="h-48 flex items-center justify-center text-slate-400">
                    <MapPin className="w-8 h-8 mr-2 opacity-50" /> No coordinates available
                  </div>
                );
              })()}
            </CardContent>
          )}
        </Card>

        {/* Trip Assignments */}
        <div className="space-y-6">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Truck className="w-5 h-5" /> Trip Assignments ({session.assignments?.length || 0})
          </h2>
          
          {session.assignments?.map((assignment, idx) => {
            const isExpanded = expandedTrips[idx];
            const viewMode = tripViewMode[idx] || 'list';
            const shipments = assignment.sublocation_assignments || [];
            
            // Calculate inwarded count for this trip
            // RETURN shipments don't need inward (picked from customer), auto-count as done
            const inwardedShipmentIds = new Set(
              (session.inwarded_shipments || []).map(s => s.shipment_id || s)
            );
            const inwardedShipmentCodes = new Set(
              (session.inwarded_shipments || []).map(s => s.shipment_code).filter(Boolean)
            );
            const forwardShipments = shipments.filter(s => !(s.is_return || s.shipment_type === "RETURN"));
            const returnShipments = shipments.filter(s => s.is_return || s.shipment_type === "RETURN");
            const tripInwardedCount = forwardShipments.filter(s => 
              inwardedShipmentIds.has(s.shipment_id) || inwardedShipmentIds.has(s.shipment_code)
            ).length + returnShipments.length;
            const tripTotalCount = shipments.length;
            const tripProgress = tripTotalCount > 0 ? (tripInwardedCount / tripTotalCount) * 100 : 0;
            
            // Sort: scanned (inwarded) first, then un-scanned at bottom. Preserve sequence within each group.
            const sortedShipments = [...shipments].sort((a, b) => {
              const aInwarded = a.is_return || a.shipment_type === "RETURN" || inwardedShipmentIds.has(a.shipment_id) || inwardedShipmentCodes.has(a.shipment_code);
              const bInwarded = b.is_return || b.shipment_type === "RETURN" || inwardedShipmentIds.has(b.shipment_id) || inwardedShipmentCodes.has(b.shipment_code);
              if (aInwarded === bInwarded) return (a.sequence || 0) - (b.sequence || 0);
              return aInwarded ? -1 : 1;
            });
            
            return (
              <Card key={idx} className="overflow-hidden">
                <CardHeader className="py-3 md:py-4 cursor-pointer hover:bg-slate-50" onClick={() => setExpandedTrips(prev => ({ ...prev, [idx]: !prev[idx] }))}>
                  <div className="flex items-start md:items-center justify-between gap-2">
                    <div className="flex items-start md:items-center gap-2 md:gap-3 flex-1 min-w-0">
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5 md:mt-0" /> : <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5 md:mt-0" />}
                      <div className="min-w-0">
                        <CardTitle className="text-sm md:text-base flex flex-wrap items-center gap-1.5 md:gap-2">
                          Trip #{assignment.trip_number}
                          {tripInwardedCount === tripTotalCount && tripTotalCount > 0 && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">Done</Badge>
                          )}
                          {assignment.bolt_trip_id && (
                            <Badge className="bg-orange-100 text-orange-700 text-xs">
                              Bolt #{assignment.bolt_trip_id}
                            </Badge>
                          )}
                          {assignment.pharmeasy_status && (
                            <Badge className={`text-xs ${
                              assignment.pharmeasy_status === "COMPLETED" || assignment.pharmeasy_status === "RECON_FINISHED"
                                ? "bg-emerald-100 text-emerald-700"
                                : assignment.pharmeasy_status === "DISPATCHED" || assignment.pharmeasy_status === "OUT_FOR_DELIVERY"
                                ? "bg-sky-100 text-sky-700"
                                : "bg-slate-100 text-slate-700"
                            }`} data-testid={`trip-pharmeasy-status-${assignment.trip_number}`}>
                              {assignment.pharmeasy_status?.replace(/_/g, " ")}
                            </Badge>
                          )}
                          {assignment.bolt_rider_assigned && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">
                              <UserCheck className="w-3 h-3 mr-0.5" /> Rider Assigned
                            </Badge>
                          )}
                          {assignment.deleted && (
                            <Badge className="bg-red-100 text-red-700 text-xs" data-testid={`trip-deleted-badge-${assignment.trip_number}`}>
                              Deleted {assignment.deleted_by ? `by ${assignment.deleted_by}` : ""} {assignment.deleted_at ? `at ${new Date(assignment.deleted_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
                            </Badge>
                          )}
                        </CardTitle>
                        <p className="text-sm text-slate-500 truncate">
                          <span className="hidden md:inline">Pincodes: </span>
                          <span className="font-medium text-slate-700">
                            {assignment.pincodes?.slice(0, 3).join(", ") || "—"}{assignment.pincodes?.length > 3 ? "..." : ""}
                          </span>
                        </p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-xs text-slate-500 hidden md:inline">Location:</span>
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 text-xs px-1.5">{assignment.location_name}</Badge>
                          {assignment.rider_name && (
                            <>
                              <span className="text-xs text-slate-400 mx-0.5">•</span>
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs px-1.5">
                                <Truck className="w-3 h-3 mr-1" />
                                {assignment.rider_name}
                              </Badge>
                            </>
                          )}
                        </div>
                        {/* Inward Time */}
                        {assignment.inward_first_scan && assignment.inward_last_scan && (
                          <p className="text-xs text-slate-500 mt-0.5">
                            <Clock className="w-3 h-3 inline mr-0.5 text-slate-400" />
                            Inward Time: <span className="font-medium text-slate-700">{formatInwardTime(assignment.inward_first_scan, assignment.inward_last_scan)}</span>
                          </p>
                        )}
                        {/* Delete Trip - for non-deleted trips: no bolt_trip_id, OR bolt_trip_id with CREATED status */}
                        {!assignment.deleted && (!assignment.bolt_trip_id || (assignment.bolt_trip_id && (!assignment.pharmeasy_status || assignment.pharmeasy_status === "CREATED"))) && !["OUT_FOR_DELIVERY", "COMPLETED", "RECON_FINISHED", "CANCELLED"].includes(assignment.pharmeasy_status) && (
                          <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); setDeleteTrip(assignment.trip_number); }}
                              disabled={deletingTrip}
                              data-testid={`delete-trip-${assignment.trip_number}`}
                              className="h-6 px-2 text-[10px] md:text-xs border-red-300 text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-3 h-3 mr-1" /> Delete Trip
                            </Button>
                          </div>
                        )}
                        {/* Bolt Trip Actions Row */}
                        {!assignment.deleted && ((tripInwardedCount === tripTotalCount && tripTotalCount > 0) || session.status === "FORCE_COMPLETED") && (session.status === "COMPLETED" || session.status === "FORCE_COMPLETED") && (
                          <div className="flex flex-wrap items-center gap-2 mt-1.5" onClick={(e) => e.stopPropagation()}>
                            {!assignment.bolt_trip_id ? (
                              <Button
                                size="sm"
                                onClick={() => handleCreateSingleBoltTrip(assignment.trip_number)}
                                disabled={isAnyBoltCreating}
                                data-testid={`create-bolt-trip-${assignment.trip_number}`}
                                className="h-6 px-2 text-[10px] md:text-xs bg-orange-500 hover:bg-orange-600 text-white"
                              >
                                {creatingBoltTrip[assignment.trip_number] ? (
                                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Creating...</>
                                ) : (
                                  <><Send className="w-3 h-3 mr-1" /> Create in Pharmeasy</>
                                )}
                              </Button>
                            ) : !assignment.bolt_rider_assigned || reassigningTrip[assignment.trip_number] ? (
                              <>
                                <select
                                  className="h-6 text-[10px] md:text-xs border rounded px-1 bg-white"
                                  value={selectedRider[assignment.trip_number] || ""}
                                  onChange={(e) => setSelectedRider(prev => ({ ...prev, [assignment.trip_number]: e.target.value }))}
                                  data-testid={`rider-select-${assignment.trip_number}`}
                                >
                                  <option value="">{assignment.bolt_rider_assigned ? "Select New Rider" : "Select Rider"}</option>
                                  {riders.filter(r => r.bolt_rider_id).map(r => (
                                    <option key={r.bolt_rider_id} value={r.bolt_rider_id}>
                                      {r.name} {r.mobile ? `(${r.mobile})` : ""}
                                    </option>
                                  ))}
                                </select>
                                <Button
                                  size="sm"
                                  onClick={() => handleAssignRider(assignment.trip_number, assignment.bolt_trip_id)}
                                  disabled={assigningRider[assignment.trip_number] || !selectedRider[assignment.trip_number]}
                                  data-testid={`assign-rider-${assignment.trip_number}`}
                                  className="h-6 px-2 text-[10px] md:text-xs bg-blue-600 hover:bg-blue-700 text-white"
                                >
                                  {assigningRider[assignment.trip_number] ? (
                                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> {assignment.bolt_rider_assigned ? "Reassigning..." : "Assigning..."}</>
                                  ) : (
                                    <><UserCheck className="w-3 h-3 mr-1" /> {assignment.bolt_rider_assigned ? "Reassign" : "Assign Rider"}</>
                                  )}
                                </Button>
                                {reassigningTrip[assignment.trip_number] && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => { setReassigningTrip(prev => ({ ...prev, [assignment.trip_number]: false })); setSelectedRider(prev => ({ ...prev, [assignment.trip_number]: "" })); }}
                                    className="h-6 px-2 text-[10px] md:text-xs text-slate-500"
                                  >
                                    Cancel
                                  </Button>
                                )}
                              </>
                            ) : (
                              !["OUT_FOR_DELIVERY", "COMPLETED", "RECON_FINISHED"].includes(assignment.pharmeasy_status) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setReassigningTrip(prev => ({ ...prev, [assignment.trip_number]: true }))}
                                data-testid={`reassign-rider-${assignment.trip_number}`}
                                className="h-6 px-2 text-[10px] md:text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                              >
                                <RefreshCw className="w-3 h-3 mr-1" /> Reassign Rider
                              </Button>
                              )
                            )}
                            {/* History toggle */}
                            {assignment.rider_assignment_history?.length > 0 && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setShowHistory(prev => ({ ...prev, [assignment.trip_number]: !prev[assignment.trip_number] }))}
                                data-testid={`rider-history-toggle-${assignment.trip_number}`}
                                className="h-6 px-2 text-[10px] md:text-xs text-slate-500 hover:text-slate-700"
                              >
                                <History className="w-3 h-3 mr-1" /> History ({assignment.rider_assignment_history.length})
                              </Button>
                            )}
                          </div>
                        )}
                        {/* Rider Assignment History */}
                        {showHistory[assignment.trip_number] && assignment.rider_assignment_history?.length > 0 && (
                          <div className="mt-2 border border-slate-200 rounded-lg bg-white p-3 max-w-md" onClick={(e) => e.stopPropagation()}>
                            <p className="text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Assignment History</p>
                            <div className="space-y-1.5">
                              {assignment.rider_assignment_history.map((h, hIdx) => (
                                <div key={hIdx} className="flex items-start gap-2 text-xs">
                                  <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${hIdx === assignment.rider_assignment_history.length - 1 ? "bg-blue-500" : "bg-slate-300"}`} />
                                  <div className="min-w-0">
                                    <span className="font-medium text-slate-800">{h.rider_name || "Unknown"}</span>
                                    {h.is_reassignment && <Badge className="ml-1 bg-amber-100 text-amber-700 text-xs px-1 py-0">Reassigned</Badge>}
                                    <p className="text-slate-400">
                                      {h.assigned_at ? new Date(h.assigned_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                                      {h.assigned_by && <span className="ml-1">by {h.assigned_by}</span>}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Add Shipment to Trip (allowed unless deleted, only before bolt dispatch) */}
                        {!assignment.deleted && (!assignment.bolt_trip_id || !["COMPLETED","CANCELLED","RECON_FINISHED"].includes(assignment.pharmeasy_status)) && (
                          <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>
                            {addingShipment[assignment.trip_number] ? (
                              <div className="flex items-center gap-1.5">
                                <Input
                                  placeholder="Shipment ID / Package ID / Ref ID"
                                  value={addShipmentCode[assignment.trip_number] || ""}
                                  onChange={(e) => setAddShipmentCode(prev => ({ ...prev, [assignment.trip_number]: e.target.value }))}
                                  className="h-6 text-[10px] md:text-xs w-36"
                                  data-testid={`add-shipment-input-${assignment.trip_number}`}
                                  onKeyDown={(e) => { if (e.key === "Enter") handleAddShipmentToTrip(assignment.trip_number); }}
                                />
                                <Button
                                  size="sm"
                                  onClick={() => handleAddShipmentToTrip(assignment.trip_number)}
                                  disabled={addShipmentLoading[assignment.trip_number]}
                                  data-testid={`add-shipment-confirm-${assignment.trip_number}`}
                                  className="h-6 px-2 text-[10px] md:text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                                >
                                  {addShipmentLoading[assignment.trip_number] ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add"}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => { setAddingShipment(prev => ({ ...prev, [assignment.trip_number]: false })); setAddShipmentCode(prev => ({ ...prev, [assignment.trip_number]: "" })); }}
                                  className="h-6 px-2 text-[10px] md:text-xs text-slate-500"
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setAddingShipment(prev => ({ ...prev, [assignment.trip_number]: true }))}
                                data-testid={`add-shipment-btn-${assignment.trip_number}`}
                                className="h-6 px-2 text-[10px] md:text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                              >
                                <Plus className="w-3 h-3 mr-1" /> Add Shipment
                              </Button>
                            )}
                          </div>
                        )}
                        {/* Modification Log Toggle */}
                        {assignment.modification_log?.length > 0 && (
                          <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setShowModLog(prev => ({ ...prev, [assignment.trip_number]: !prev[assignment.trip_number] }))}
                              data-testid={`mod-log-toggle-${assignment.trip_number}`}
                              className="h-6 px-2 text-[10px] md:text-xs text-slate-500 hover:text-slate-700"
                            >
                              <History className="w-3 h-3 mr-1" /> Changes ({assignment.modification_log.length})
                            </Button>
                          </div>
                        )}
                        {/* Modification Log Display */}
                        {showModLog[assignment.trip_number] && assignment.modification_log?.length > 0 && (
                          <div className="mt-2 border border-slate-200 rounded-lg bg-white p-3 max-w-md" onClick={(e) => e.stopPropagation()}>
                            <p className="text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Modification Log</p>
                            <div className="space-y-1.5">
                              {[...assignment.modification_log].reverse().map((entry, eIdx) => (
                                <div key={eIdx} className="flex items-start gap-2 text-xs">
                                  <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${entry.action === "added" ? "bg-emerald-500" : "bg-red-500"}`} />
                                  <div className="min-w-0">
                                    <span className={`font-medium ${entry.action === "added" ? "text-emerald-700" : "text-red-700"}`}>
                                      {entry.action === "added" ? "Added" : "Removed"}
                                    </span>
                                    <span className="text-slate-600 ml-1 font-mono">{entry.shipment_code}</span>
                                    {entry.sublocation_name && <span className="text-slate-400 ml-1">({entry.sublocation_name})</span>}
                                    <p className="text-slate-400">
                                      {entry.added_at || entry.removed_at
                                        ? new Date(entry.added_at || entry.removed_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
                                        : "—"}
                                      {(entry.added_by || entry.removed_by) && <span className="ml-1">by {entry.added_by || entry.removed_by}</span>}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 md:gap-1 flex-shrink-0">
                      <span className="text-sm md:text-lg font-bold text-blue-600">{assignment.shipment_count}<span className="text-xs md:text-sm font-normal ml-1 hidden sm:inline">shipments</span></span>
                      <span className={`text-xs md:text-sm font-semibold ${tripInwardedCount === tripTotalCount && tripTotalCount > 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {tripInwardedCount - returnShipments.length}/{forwardShipments.length}
                        {returnShipments.length > 0 && (
                          <span className="text-purple-600 text-xs font-normal ml-1">+ {returnShipments.length} pickup</span>
                        )}
                      </span>
                      {tripTotalCount > 0 && (
                        <div className="w-16 md:w-24 h-1 md:h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${tripInwardedCount === tripTotalCount ? 'bg-emerald-500' : 'bg-amber-500'}`}
                            style={{ width: `${tripProgress}%` }}
                          />
                        </div>
                      )}
                      {assignment.pharmeasy_cash_to_collect > 0 && (
                        <span className="text-xs text-slate-500 hidden md:inline" data-testid={`trip-cash-info-${assignment.trip_number}`}>
                          <IndianRupee className="w-2.5 h-2.5 inline" />{(assignment.pharmeasy_cash_collected || 0).toLocaleString("en-IN")} / {assignment.pharmeasy_cash_to_collect.toLocaleString("en-IN")}
                        </span>
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                {isExpanded && (
                  <CardContent className="border-t bg-slate-50 pt-4 px-4 md:px-6">
                    {/* View Toggle */}
                    <div className="flex items-center justify-between mb-3 md:mb-4">
                      <p className="text-sm font-medium text-slate-600">Shipment Details</p>
                      <div className="flex items-center gap-0.5 md:gap-1 bg-white rounded-lg border p-0.5 md:p-1">
                        <Button
                          variant={viewMode === 'list' ? 'default' : 'ghost'}
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setTripViewMode(prev => ({ ...prev, [idx]: 'list' })); }}
                          className={`text-xs md:text-sm px-2 md:px-3 h-7 md:h-9 ${viewMode === 'list' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                        >
                          <List className="w-3.5 h-3.5 md:w-4 md:h-4 mr-0.5 md:mr-1" /> <span className="hidden sm:inline">List</span>
                        </Button>
                        <Button
                          variant={viewMode === 'map' ? 'default' : 'ghost'}
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setTripViewMode(prev => ({ ...prev, [idx]: 'map' })); }}
                          className={`text-xs md:text-sm px-2 md:px-3 h-7 md:h-9 ${viewMode === 'map' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                        >
                          <MapIcon className="w-3.5 h-3.5 md:w-4 md:h-4 mr-0.5 md:mr-1" /> <span className="hidden sm:inline">Map</span>
                        </Button>
                      </div>
                    </div>
                    
                    {viewMode === 'list' ? (
                      /* List View - Mobile Card View / Desktop Table */
                      <>
                        {/* Desktop Table */}
                        <div className="hidden md:block max-h-96 overflow-y-auto bg-white rounded-lg border overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-100 sticky top-0">
                              <tr>
                                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap w-8">Seq</th>
                                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap w-16">Type</th>
                                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Shipment ID</th>
                                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Package ID</th>
                                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Reference ID</th>
                                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">LBN</th>
                                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap w-16">Pincode</th>
                                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Address</th>
                                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap w-14">Pay</th>
                                <th className="px-3 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap w-12">Val</th>
                                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap w-12">Slot</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap w-20">Inwarded</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap w-8">Map</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap w-10"></th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                              {sortedShipments.map((sub, subIdx) => {
                                const mapLink = getMapLink(sub);
                                const fullAddress = formatAddress(sub.dest_address);
                                // Check if this shipment is inwarded
                                const inwardInfo = (session.inwarded_shipments || []).find(
                                  i => i.shipment_id === sub.shipment_id || i.shipment_code === sub.shipment_code
                                );
                                const isInwarded = !!inwardInfo;
                                const isReturn = sub.is_return || sub.shipment_type === "RETURN";
                                return (
                                  <tr key={subIdx} className={`hover:bg-slate-50 ${isInwarded ? 'bg-emerald-50/50' : ''} ${isReturn ? 'bg-purple-50/50' : ''}`}>
                                    <td className="px-3 py-3 align-middle text-slate-500">{sub.sequence}</td>
                                    <td className="px-3 py-3 align-middle">
                                      <Badge className={`text-xs px-1 py-0 ${isReturn ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                                        {sub.shipment_type || (isReturn ? "RETURN" : "FORWARD")}
                                      </Badge>
                                    </td>
                                    <td className="px-3 py-3 align-middle">
                                      {sub.shipment_code ? (
                                        <div className="flex items-center gap-0.5">
                                          <button
                                            onClick={() => window.open(`/pharmeasy/orders/${sub.shipment_id}`, '_blank')}
                                            className="font-mono text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-0.5"
                                          >
                                            {sub.shipment_code}
                                            <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
                                          </button>
                                          <button 
                                            onClick={(e) => { e.stopPropagation(); copyToClipboard(sub.shipment_code, 'Shipment ID'); }} 
                                            className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-0.5 rounded flex-shrink-0"
                                            title="Copy Shipment ID"
                                          >
                                            <Copy className="w-3 h-3" />
                                          </button>
                                        </div>
                                      ) : (
                                        <span className="font-mono text-sm text-slate-400">—</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-3 align-middle">
                                      {sub.package_id ? (
                                        <div className="flex items-center gap-0.5">
                                          <button
                                            onClick={() => window.open(`/pharmeasy/orders/${sub.shipment_id}`, '_blank')}
                                            className="font-mono text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-0.5 truncate max-w-[120px]"
                                          >
                                            {sub.package_id}
                                            <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
                                          </button>
                                          <button 
                                            onClick={(e) => { e.stopPropagation(); copyToClipboard(sub.package_id, 'Package ID'); }} 
                                            className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-0.5 rounded flex-shrink-0"
                                            title="Copy Package ID"
                                          >
                                            <Copy className="w-3 h-3" />
                                          </button>
                                        </div>
                                      ) : (
                                        <span className="font-mono text-sm text-slate-400">—</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-3 align-middle">
                                      {sub.reference_id ? (
                                        <div className="flex items-center gap-0.5">
                                          <span className="font-mono text-sm text-slate-600 truncate max-w-[90px]">{sub.reference_id}</span>
                                          <button 
                                            onClick={(e) => { e.stopPropagation(); copyToClipboard(sub.reference_id, 'Reference ID'); }} 
                                            className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-0.5 rounded flex-shrink-0"
                                            title="Copy Reference ID"
                                          >
                                            <Copy className="w-3 h-3" />
                                          </button>
                                        </div>
                                      ) : (
                                        <span className="font-mono text-sm text-slate-400">—</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-3 align-middle">
                                      {sub.lbn ? (
                                        <div className="flex items-center gap-0.5">
                                          <span className="font-mono text-sm text-slate-600">{sub.lbn}</span>
                                          <button 
                                            onClick={(e) => { e.stopPropagation(); copyToClipboard(sub.lbn, 'LBN'); }} 
                                            className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-0.5 rounded flex-shrink-0"
                                            title="Copy LBN"
                                          >
                                            <Copy className="w-3 h-3" />
                                          </button>
                                        </div>
                                      ) : (
                                        <span className="font-mono text-sm text-slate-400">—</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-3 align-middle text-sm text-slate-600">{sub.drop_pincode || "—"}</td>
                                    <td className="px-3 py-3 align-middle max-w-[120px] group relative">
                                      <div className="flex items-center gap-0.5">
                                        <span className="text-sm text-slate-500 truncate block cursor-help" title={fullAddress}>
                                          {fullAddress}
                                        </span>
                                        {fullAddress && fullAddress !== "—" && (
                                          <button 
                                            onClick={(e) => { e.stopPropagation(); copyToClipboard(fullAddress, 'Address'); }} 
                                            className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-0.5 rounded flex-shrink-0"
                                            title="Copy Address"
                                          >
                                            <Copy className="w-3 h-3" />
                                          </button>
                                        )}
                                      </div>
                                      {fullAddress && fullAddress !== "—" && fullAddress.length > 25 && (
                                        <div className="absolute z-50 hidden group-hover:block bg-slate-800 text-white text-xs rounded-lg p-2 max-w-[300px] left-0 top-full mt-1 shadow-lg whitespace-normal">
                                          {fullAddress}
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-3 py-3 align-middle">
                                      <Badge variant="outline" className={`text-xs px-1 py-0 ${
                                        sub.payment_type === "COD" ? "text-amber-600 border-amber-300" : "text-green-600 border-green-300"
                                      }`}>
                                        {sub.payment_type || "—"}
                                      </Badge>
                                    </td>
                                    <td className="px-3 py-3 align-middle text-right">
                                      <span className="text-sm font-medium">
                                        {sub.shipment_value ? `₹${Number(sub.shipment_value).toLocaleString()}` : "—"}
                                      </span>
                                    </td>
                                    <td className="px-3 py-3 align-middle">
                                      {isReturn ? (
                                        <Badge variant="outline" className="text-xs px-1 py-0 bg-purple-50 text-purple-600">PICKUP</Badge>
                                      ) : (
                                        <Badge variant="outline" className="text-xs px-1 py-0">{sub.sublocation_name}</Badge>
                                      )}
                                    </td>
                                    <td className="px-3 py-3 align-middle text-center">
                                      {isReturn ? (
                                        <Badge className="bg-purple-100 text-purple-700 text-xs px-1 py-0">Pickup</Badge>
                                      ) : isInwarded ? (
                                        <div className="flex flex-col items-center">
                                          <Badge className="bg-emerald-100 text-emerald-700 text-xs px-1 py-0">
                                            <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Done
                                          </Badge>
                                          <span className="text-xs text-slate-500 mt-0.5">{inwardInfo.inwarded_by}</span>
                                          <div className="whitespace-nowrap text-xs text-slate-400 text-center">
                                            <div>{formatDate(inwardInfo.inwarded_at).split(', ')[0]}</div>
                                            <div>{formatDate(inwardInfo.inwarded_at).split(', ')[1]}</div>
                                          </div>
                                        </div>
                                      ) : (
                                        <Badge variant="outline" className="text-slate-400 text-xs px-1 py-0">Pending</Badge>
                                      )}
                                    </td>
                                    <td className="px-3 py-3 align-middle text-center">
                                      {mapLink ? (
                                        <a href={mapLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                                          <Navigation className="w-3.5 h-3.5 inline" />
                                        </a>
                                      ) : "—"}
                                    </td>
                                    <td className="px-3 py-3 align-middle text-center">
                                      {!assignment.bolt_trip_id && !isInwarded && !isReturn && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setRemoveConfirm({ tripNumber: assignment.trip_number, shipmentCode: sub.shipment_code, shipmentId: sub.shipment_id }); }}
                                          className="text-slate-400 hover:text-red-600 hover:bg-red-50 p-0.5 rounded"
                                          title="Remove from trip"
                                          data-testid={`remove-shipment-${sub.shipment_code}`}
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Mobile Card View */}
                        <div className="md:hidden space-y-2 max-h-[400px] overflow-y-auto">
                          {sortedShipments.map((sub, subIdx) => {
                            const mapLink = getMapLink(sub);
                            const fullAddress = formatAddress(sub.dest_address);
                            // Check if this shipment is inwarded
                            const inwardInfo = (session.inwarded_shipments || []).find(
                              i => i.shipment_id === sub.shipment_id || i.shipment_code === sub.shipment_code
                            );
                            const isInwarded = !!inwardInfo;
                            const isReturn = sub.is_return || sub.shipment_type === "RETURN";
                            return (
                              <div key={subIdx} className={`bg-white rounded-lg border p-4 ${isInwarded ? 'border-emerald-300 bg-emerald-50/30' : ''} ${isReturn ? 'border-purple-300 bg-purple-50/30' : ''}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${isInwarded ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
                                      {isInwarded ? <CheckCircle2 className="w-4 h-4" /> : sub.sequence}
                                    </span>
                                    <Badge className={`text-xs ${isReturn ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                                      {sub.shipment_type || (isReturn ? "RETURN" : "FORWARD")}
                                    </Badge>
                                    {!isReturn && (
                                      <Badge variant="outline" className="text-xs">{sub.sublocation_name}</Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {isInwarded && (
                                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">Done</Badge>
                                    )}
                                    {!assignment.bolt_trip_id && !isInwarded && !isReturn && (
                                      <button
                                        onClick={() => setRemoveConfirm({ tripNumber: assignment.trip_number, shipmentCode: sub.shipment_code, shipmentId: sub.shipment_id })}
                                        className="text-slate-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg"
                                        title="Remove from trip"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                    {mapLink && (
                                      <a href={mapLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 p-1.5 bg-blue-50 rounded-lg">
                                        <Navigation className="w-4 h-4" />
                                      </a>
                                    )}
                                  </div>
                                </div>
                                <div className="space-y-1 text-xs">
                                  <div className="flex justify-between items-center">
                                    <span className="text-slate-500">Shipment:</span>
                                    {sub.shipment_code ? (
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={() => window.open(`/pharmeasy/orders/${sub.shipment_id}`, '_blank')}
                                          className="font-mono text-sm font-medium text-blue-600 hover:underline flex items-center gap-1"
                                        >
                                          {sub.shipment_code} <ExternalLink className="w-3 h-3" />
                                        </button>
                                        <button onClick={() => copyToClipboard(sub.shipment_code, 'Shipment ID')} className="text-slate-400 hover:text-slate-600 p-0.5">
                                          <Copy className="w-3 h-3" />
                                        </button>
                                      </div>
                                    ) : (
                                      <span className="text-slate-400">—</span>
                                    )}
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-slate-500">Package:</span>
                                    <div className="flex items-center gap-1">
                                      <span className="font-mono text-sm font-medium">{sub.package_id || "—"}</span>
                                      {sub.package_id && (
                                        <button onClick={() => copyToClipboard(sub.package_id, 'Package ID')} className="text-slate-400 hover:text-slate-600 p-0.5">
                                          <Copy className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-slate-500">Reference:</span>
                                    <div className="flex items-center gap-1">
                                      <span className="font-mono text-sm font-medium">{sub.reference_id || "—"}</span>
                                      {sub.reference_id && (
                                        <button onClick={() => copyToClipboard(sub.reference_id, 'Reference ID')} className="text-slate-400 hover:text-slate-600 p-0.5">
                                          <Copy className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-slate-500">LBN:</span>
                                    <div className="flex items-center gap-1">
                                      <span className="font-mono text-sm font-medium">{sub.lbn || "—"}</span>
                                      {sub.lbn && (
                                        <button onClick={() => copyToClipboard(sub.lbn, 'LBN')} className="text-slate-400 hover:text-slate-600 p-0.5">
                                          <Copy className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Pincode:</span>
                                    <span className="text-sm font-medium">{sub.drop_pincode || "—"}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Payment:</span>
                                    <Badge variant="outline" className={`text-xs ${
                                      sub.payment_type === "COD" ? "text-amber-600 border-amber-300" : "text-green-600 border-green-300"
                                    }`}>
                                      {sub.payment_type || "—"}
                                    </Badge>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Value:</span>
                                    <span className="text-sm font-medium">{sub.shipment_value ? `₹${Number(sub.shipment_value).toLocaleString()}` : "—"}</span>
                                  </div>
                                  {fullAddress && fullAddress !== "—" && (
                                    <div className="flex items-start gap-1 pt-1">
                                      <span className="text-sm text-slate-500 truncate flex-1">{fullAddress}</span>
                                      <button onClick={() => copyToClipboard(fullAddress, 'Address')} className="text-slate-400 hover:text-slate-600 p-0.5 flex-shrink-0">
                                        <Copy className="w-3 h-3" />
                                      </button>
                                    </div>
                                  )}
                                  {isInwarded && (
                                    <div className="mt-2 pt-2 border-t border-emerald-200 text-emerald-700">
                                      <div className="flex justify-between">
                                        <span>Inwarded by:</span>
                                        <span className="font-medium">{inwardInfo.inwarded_by}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span>At:</span>
                                        <span>{formatDate(inwardInfo.inwarded_at)}</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      /* Map View - Interactive Google Maps */
                      <div className="bg-white rounded-lg border overflow-hidden">
                        {(() => {
                          const embedUrl = buildDirectionsEmbedUrl(shipments);
                          const staticUrl = buildStaticMapUrl(shipments, 12);
                          
                          if (embedUrl) {
                            return (
                              <div className="relative">
                                {/* Interactive Google Maps iframe */}
                                <iframe
                                  src={embedUrl}
                                  width="100%"
                                  height="450"
                                  style={{ border: 0 }}
                                  allowFullScreen=""
                                  loading="lazy"
                                  referrerPolicy="no-referrer-when-downgrade"
                                  title={`Trip ${assignment.trip_number} Route Map`}
                                  className="w-full"
                                />
                                
                                {/* Delivery Sequence panel */}
                                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-lg p-3 max-h-64 overflow-y-auto shadow-lg">
                                  <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-2">
                                    <Truck className="w-4 h-4" /> Delivery Sequence:
                                  </p>
                                  {shipments.slice(0, 15).map((sub, i) => (
                                    <div key={i} className="text-xs text-slate-600 flex items-center gap-2 py-1">
                                      <span className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i+1}</span>
                                      <span className="truncate max-w-[180px]">{sub.shipment_code} - {sub.dest_address?.city || sub.drop_pincode}</span>
                                    </div>
                                  ))}
                                  {shipments.length > 15 && (
                                    <p className="text-xs text-slate-400 mt-1">+{shipments.length - 15} more stops</p>
                                  )}
                                </div>
                                
                                {/* Open in Google Maps button */}
                                <a 
                                  href={(() => {
                                    const hubCoords = session?.hub_coordinates;
                                    const hubStart = hubCoords?.lat && hubCoords?.lng ? `${hubCoords.lat},${hubCoords.lng}/` : '';
                                    const deliveryPoints = shipments.map(s => {
                                      const calc = s.calculated_dest_address_coordinate;
                                      if (calc?.success && calc?.coordinates) {
                                        return `${calc.coordinates.lat},${calc.coordinates.lng}`;
                                      }
                                      return null;
                                    }).filter(Boolean).join('/');
                                    return `https://www.google.com/maps/dir/${hubStart}${deliveryPoints}`;
                                  })()}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="absolute bottom-4 right-4 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 flex items-center gap-2 shadow-lg"
                                >
                                  <Navigation className="w-4 h-4" /> Open Full Route
                                </a>
                              </div>
                            );
                          } else if (staticUrl) {
                            return (
                              <div className="relative">
                                <img 
                                  src={staticUrl} 
                                  alt={`Trip ${assignment.trip_number} Map`}
                                  className="w-full h-[400px] object-cover"
                                />
                                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur rounded-lg p-3 max-h-48 overflow-y-auto">
                                  <p className="text-xs font-semibold text-slate-600 mb-2">Delivery Sequence:</p>
                                  {shipments.slice(0, 15).map((sub, i) => (
                                    <div key={i} className="text-xs text-slate-600 flex items-center gap-2">
                                      <span className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-[10px] font-bold">{i+1}</span>
                                      <span className="truncate max-w-[200px]">{sub.shipment_code} - {sub.dest_address?.city || sub.drop_pincode}</span>
                                    </div>
                                  ))}
                                  {shipments.length > 15 && (
                                    <p className="text-xs text-slate-400 mt-1">+{shipments.length - 15} more</p>
                                  )}
                                </div>
                              </div>
                            );
                          } else {
                            return (
                              <div className="h-[400px] flex items-center justify-center text-slate-400">
                                <div className="text-center">
                                  <MapPin className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                  <p>No coordinates available for map view</p>
                                </div>
                              </div>
                            );
                          }
                        })()}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Inward Scanner Modal */}
      <InwardScanner
        isOpen={scannerOpen}
        onClose={() => { setScannerOpen(false); fetchSessionDetail(); }}
        sessionId={sessionId}
        hubId={session?.hub_id}
        onScanSuccess={handleScanSuccess}
        sessionData={session}
      />

      {/* Remove Shipment Confirmation Dialog */}
      <Dialog open={!!removeConfirm} onOpenChange={(open) => { if (!open) setRemoveConfirm(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Remove Shipment
            </DialogTitle>
            <DialogDescription>
              <span className="font-mono font-semibold text-slate-800">{removeConfirm?.shipmentCode}</span> will be removed from <span className="font-semibold text-slate-800">Trip #{removeConfirm?.tripNumber}</span> and will go into the unassigned section. There will be no change in slot position of other shipments in this trip.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setRemoveConfirm(null)}
              disabled={removingShipment}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRemoveShipment}
              disabled={removingShipment}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-remove-shipment"
            >
              {removingShipment ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Removing...</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Remove</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Trip Confirmation Dialog */}
      <Dialog open={deleteTrip !== null} onOpenChange={(open) => { if (!open) setDeleteTrip(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Trip #{deleteTrip}
            </DialogTitle>
            <DialogDescription>
              All shipments in <span className="font-semibold text-slate-800">Trip #{deleteTrip}</span> will be moved to the <span className="font-semibold text-slate-800">Unassigned</span> section. The trip will remain visible with a "Deleted" status. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteTrip(null)}
              disabled={deletingTrip}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteTrip}
              disabled={deletingTrip}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-trip"
            >
              {deletingTrip ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Delete Trip</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Complete Confirmation Modal */}
      <Dialog open={forceCompleteModalOpen} onOpenChange={setForceCompleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Force Complete Session
            </DialogTitle>
            <DialogDescription>
              This will mark the session as completed even though {totalShipments - effectiveInwardedCount} parcel(s) 
              have not been scanned. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-800">
                <strong>Current Progress:</strong> {inwardedCount} / {totalShipments} parcels inwarded
              </p>
              <p className="text-sm text-amber-700 mt-1">
                {totalShipments - inwardedCount} parcel(s) will be marked as not scanned.
              </p>
              <p className="text-sm text-amber-800 mt-2 font-medium">
                Status of all Forward shipments that are not inwarded will be updated to Unassigned.
              </p>
            </div>
            <label className="text-sm font-medium text-slate-700">
              Reason for force completion (optional)
            </label>
            <Textarea
              value={forceCompleteReason}
              onChange={(e) => setForceCompleteReason(e.target.value)}
              placeholder="e.g., Parcels damaged, shipment cancelled, etc."
              className="mt-2"
              rows={3}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setForceCompleteModalOpen(false)}
              disabled={forceCompleting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleForceComplete}
              disabled={forceCompleting}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {forceCompleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...
                </>
              ) : (
                <>
                  <AlertTriangle className="w-4 h-4 mr-2" /> Force Complete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PharmeasyLayout>
  );
};

export default InwardSessionDetail;
